/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { createFeature, createReducer, on } from '@ngrx/store';
import {
    acdAnswerComplete,
    acdAnswerCompleteFailed,
    acdAnswerRequest,
    acdAnswerRequestFail,
    acdBid,
    acdBidFail,
    acdBidRemove,
    acdBidResponse,
    acdRejection,
    acdStatusChangeFail,
    acdStatusChangeSuccess,
    acknowledgeAbandonedComplete,
    clearUsersSubscribedToCluster,
    clusterLogoutRequest,
    clusterLogoutRequestFail,
    clusterLogoutRequestSuccess,
    deleteManyUsers,
    deleteUserAuthenticationRecord,
    initUser,
    languageSelect,
    newUserAuthenticationRecord,
    remoteLogoutEvent,
    removeIgnoredAcdCall,
    requestedAcdStatusChange,
    acdReasonPickerOpenChanged,
    refuseAbandonedRequestSuccess,
    setIgnoredAcdCalls,
    setManyUsers,
    setRole,
    updateApplicationPresence,
    updateLocalApplicationPresence,
    updateUserAuthenticationRecord,
    updateUserMultipleSubscriptions,
    userEffectsInitialized
} from './user.actions';
import { fetchReasonCodesSuccess } from './reason-code.actions';
import { AgentStatusRecord, Cluster, LogoutRequest, UserAuthenticationRecord } from 'CalltakingCoreApi';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { BidResult } from '../model/bid-result';
import { VestaKeycloakTokenParsed } from '../model/vesta-keycloak-token-parsed';
import { SortFunctions } from '../../call/util/sort-functions';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { fetchAvailableLanguagesSuccess, fetchUserSelectableRolesSuccess, initSubscriptionState } from '../../configuration/+state/configuration.actions';
import { ReasonCode } from '../model/reason-code';
import { RequestedAgentStatus } from '../model/requested-agent-status';
import { MultipleSubscriptionsStatus, Topic } from '../model/multiple-subscriptions';
import { answerSuccess, redialAbandonedSuccess } from '../../call/+state/call.actions';

dayjs.extend(duration);

export interface UserState extends EntityState<UserAuthenticationRecord> {
    effectsInitialized: boolean;
    multipleSubscriptions: MultipleSubscriptionsStatus;
    applicationPresence: {
        loggedIn: boolean;
        loginTime?: number;
    };
    localApplicationPresence: {
        [clusterName: string]: {
            loggedIn: boolean;
            loginTime?: number;
        };
    };
    token: string | null;
    tokenParsed: VestaKeycloakTokenParsed | undefined;
    agentStatus: AgentStatusRecord | null;
    requestedAgentStatus: RequestedAgentStatus;
    wrapUpDuration: number;
    wrapUpStartTime: number;
    notReadyOnRelease: boolean;
    pendingBid: string | null;
    pendingAcdAnswer: { callId: string; nenaId: string; clusterName: string } | undefined;
    mostRecentlyAnsweredAcdNenaCallId: string | undefined;
    bids: BidState;
    acdIgnoredCalls: {};
    queues: string[];
    supervisedQueues: string[];
    supervisorQueue: string | undefined;
    selectedRole: string | undefined;
    language: string;
    agentFilters: string[];
    permissions: string[];
    autoLogoffRequest: LogoutRequest | undefined;
    agencyName: string;
    agencyFqdn: string;
    supportedLanguages: string[];
    acknowledgeAbandonedCallId: string | undefined;
    mostRecentHandledCallIsAbandoned: boolean;
    remoteLogoutStatus: {
        [clusterName: string]: {
            inProgress: boolean;
            logoffSuccessful: boolean;
            onCall: boolean;
        };
    };
    position_id: number;
    reasonCodes: ReasonCode[];
    previousAgentStatus: AgentStatusRecord | null;
    acdReasonPickerOpen: boolean;
}

export interface BidState extends EntityState<BidResult> {}

export const bidAdapter: EntityAdapter<BidResult> = createEntityAdapter<BidResult>({
    selectId: (bidResult: BidResult) => bidResult.callId,
    sortComparer: SortFunctions.bidSort
});

export const adapter: EntityAdapter<UserAuthenticationRecord> = createEntityAdapter<UserAuthenticationRecord>({
    selectId: (authenticationRecord: UserAuthenticationRecord) => authenticationRecord.uuid
});

const initialBidState: BidState = bidAdapter.getInitialState({});

const initialState: UserState = adapter.getInitialState({
    effectsInitialized: false,
    multipleSubscriptions: {},
    applicationPresence: {
        loggedIn: false
    },
    localApplicationPresence: {},
    token: null,
    tokenParsed: undefined,
    agentStatus: null,
    requestedAgentStatus: {
        status: 'NOT_READY',
        isAgentRequested: false
    },
    wrapUpDuration: 0,
    wrapUpStartTime: 0,
    notReadyOnRelease: false,
    pendingBid: null,
    pendingAcdAnswer: undefined,
    mostRecentlyAnsweredAcdNenaCallId: undefined,
    acknowledgeAbandonedCallId: undefined,
    mostRecentHandledCallIsAbandoned: false,
    bids: initialBidState,
    acdIgnoredCalls: {},
    queues: [],
    supervisedQueues: [],
    supervisorQueue: undefined,
    selectedRole: undefined,
    language: 'en-US',
    agentFilters: [],
    permissions: [],
    autoLogoffRequest: undefined,
    agencyName: '',
    agencyFqdn: '',
    supportedLanguages: [],
    remoteLogoutStatus: {},
    position_id: 0,
    reasonCodes: [],
    previousAgentStatus: null,
    acdReasonPickerOpen: false
});

export const userFeature = createFeature({
    name: 'user',
    reducer: createReducer(
        initialState,
        on(userEffectsInitialized, (state) => ({ ...state, effectsInitialized: true })),
        on(acdReasonPickerOpenChanged, (state, { open }): UserState => ({ ...state, acdReasonPickerOpen: open })),
        on(initSubscriptionState, (state, { config }) => {
            const multipleSubscriptions: MultipleSubscriptionsStatus = {};
            config.clusters.forEach((c: Cluster) => {
                multipleSubscriptions[c.name] = {
                    [Topic.USER_LOGOUT]: false,
                    [Topic.USER_AUTHENTICATION_DELETE]: false,
                    [Topic.USER_AUTHENTICATION_NEW]: false,
                    [Topic.USER_AUTHENTICATION_EDIT]: false,
                    [Topic.FORCED_LOGOUT]: false
                };
            });
            return { ...state, multipleSubscriptions };
        }),
        on(updateUserMultipleSubscriptions, (state, { topic, clusterName, status }): UserState => {
            const currentSubscriptions = state.multipleSubscriptions;
            const subscriptions = {
                ...currentSubscriptions,
                [clusterName]: {
                    ...currentSubscriptions[clusterName],
                    [topic]: status
                }
            };
            return { ...state, multipleSubscriptions: subscriptions };
        }),
        on(initUser, (state, { token, tokenParsed }): UserState => {
            let selectedRole = state.selectedRole ? state.selectedRole : tokenParsed?.preferred_role ? tokenParsed?.preferred_role : undefined;
            let queues = tokenParsed?.preferred_role_queues ? tokenParsed?.preferred_role_queues : [];
            let supervisedQueues = tokenParsed?.preferred_role_supervised_queues ? tokenParsed?.preferred_role_supervised_queues : [];
            let permissions = tokenParsed && tokenParsed.preferred_role_permissions ? tokenParsed?.preferred_role_permissions : [];
            let supervisorQueue = tokenParsed?.preferred_role_supervisor_queue ? JSON.parse(tokenParsed?.preferred_role_supervisor_queue)[0] : undefined;
            let agencyName = tokenParsed?.agency_name;
            let agencyFqdn = tokenParsed?.agency_fqdn;
            let position = tokenParsed?.position_id;
            return {
                ...state,
                token: token,
                tokenParsed: tokenParsed,
                language: navigator.language,
                queues: queues,
                supervisedQueues: supervisedQueues,
                selectedRole: selectedRole,
                permissions: permissions,
                supervisorQueue: supervisorQueue,
                agencyName,
                agencyFqdn,
                position_id: position
            };
        }),
        on(languageSelect, (state, { language }): UserState => {
            return { ...state, language: language };
        }),
        on(updateApplicationPresence, (state, { loggedIn, loginTime }): UserState => {
            return { ...state, applicationPresence: { loggedIn: loggedIn, loginTime: loginTime } };
        }),
        on(updateLocalApplicationPresence, (state, { loggedIn, loginTime, clusterName }): UserState => {
            return {
                ...state,
                localApplicationPresence: {
                    ...state.localApplicationPresence,
                    [clusterName]: { loggedIn: loggedIn, loginTime: loginTime }
                }
            };
        }),
        on(newUserAuthenticationRecord, updateUserAuthenticationRecord, (state, { userAuthenticationRecord }) => {
            let existingEntity = state.entities[userAuthenticationRecord.uuid];
            if (!existingEntity || dayjs(existingEntity.updatedTimestamp).isBefore(dayjs(userAuthenticationRecord.updatedTimestamp))) {
                return adapter.setOne(userAuthenticationRecord, state);
            }
            return state;
        }),
        on(deleteUserAuthenticationRecord, (state, { userAuthenticationRecord }) => {
            let existingEntity = state.entities[userAuthenticationRecord.uuid];
            if (existingEntity) {
                return adapter.removeOne(userAuthenticationRecord.uuid, state);
            }
            return state;
        }),
        on(setManyUsers, (state, { users }) => {
            let newOrUpdatedUsers = users.filter(
                (user) => !state.entities[user.uuid] || dayjs(state.entities[user.uuid].updatedTimestamp).isBefore(dayjs(user.updatedTimestamp))
            );
            return adapter.setMany(newOrUpdatedUsers, state);
        }),
        on(deleteManyUsers, (state, { userIds }) => {
            return adapter.removeMany(userIds, state);
        }),
        on(acdBid, (state, { bid }): UserState => {
            return { ...state, pendingBid: bid.callId };
        }),
        on(acdBidResponse, (state, { result }) => {
            return {
                ...state,
                bids: bidAdapter.upsertOne(result, state.bids),
                pendingBid: null,
                wrapUpDuration: result.queueACD?.wrapup ? dayjs.duration(result.queueACD.wrapup).as('milliseconds') : 0,
                notReadyOnRelease: Boolean(result.queueACD?.notReadyOnRelease)
            };
        }),
        on(acdBidFail, (state) => {
            return { ...state, pendingBid: null };
        }),
        on(acdAnswerRequest, (state, { bid }) => {
            return {
                ...state,
                pendingAcdAnswer: { callId: bid.callId, nenaId: bid.nenaId, clusterName: bid.clusterName }
            };
        }),
        on(acdAnswerComplete, (state, { nenaId }) => {
            return { ...state, pendingAcdAnswer: undefined, mostRecentlyAnsweredAcdNenaCallId: nenaId, mostRecentHandledCallIsAbandoned: false };
        }),
        on(acdAnswerRequestFail, acdAnswerCompleteFailed, (state): UserState => {
            return { ...state, pendingAcdAnswer: undefined };
        }),
        on(acdBidRemove, (state, { callId }) => {
            return { ...state, bids: bidAdapter.removeOne(callId, state.bids) };
        }),
        on(acdRejection, (state, { event }): UserState => {
            if (state.bids.entities[event.callId]) {
                let bidToReject = { ...state.bids.entities[event.callId] } as BidResult;
                bidToReject.result = 'ASSIGNEE_GONE';
                return {
                    ...state,
                    bids: bidAdapter.upsertOne(bidToReject, state.bids)
                };
            }
            return { ...state };
        }),
        on(setIgnoredAcdCalls, (state, { calls }): UserState => {
            let ignoredCalls: any = { ...state.acdIgnoredCalls };
            calls.forEach((c) => (ignoredCalls[c] = c));
            return { ...state, acdIgnoredCalls: ignoredCalls };
        }),
        on(removeIgnoredAcdCall, (state, { callId }): UserState => {
            let ignoredCalls: any = { ...state.acdIgnoredCalls };
            delete ignoredCalls[callId];
            return { ...state, acdIgnoredCalls: ignoredCalls };
        }),
        on(acdStatusChangeSuccess, (state, { agentStatus }): UserState => {
            return {
                ...state,
                agentStatus: agentStatus,
                previousAgentStatus: null,
                requestedAgentStatus:
                    agentStatus.status === 'WRAPUP'
                        ? {
                              status: 'WRAPUP',
                              isAgentRequested: false
                          }
                        : state.requestedAgentStatus,
                wrapUpStartTime: agentStatus && agentStatus.date && agentStatus?.status === 'WRAPUP' ? new Date(agentStatus.date).getTime() : 0
            };
        }),
        on(requestedAcdStatusChange, (state, { agentStatus, isAgentRequested, statusReasonDisplayName, statusReasonId }): UserState => {
            return {
                ...state,
                requestedAgentStatus: { status: agentStatus, isAgentRequested: isAgentRequested, statusReasonDisplayName: statusReasonDisplayName, statusReasonId: statusReasonId },
                previousAgentStatus: state.agentStatus,
                agentStatus: state.agentStatus ? {
                    ...state.agentStatus,
                    statusReasonDisplayName: statusReasonDisplayName || state.agentStatus.statusReasonDisplayName
                } : state.agentStatus
            };
        }),
        on(setRole, (state, { role }): UserState => {
            return { ...state, selectedRole: role };
        }),
        on(remoteLogoutEvent, (state, { logoutRequest }): UserState => {
            return { ...state, autoLogoffRequest: logoutRequest };
        }),
        on(clusterLogoutRequest, (state, { clusterName }): UserState => {
            let remoteLogoutStatus = { ...state.remoteLogoutStatus };
            remoteLogoutStatus[clusterName] = {
                ...remoteLogoutStatus[clusterName],
                inProgress: true,
                onCall: false,
                logoffSuccessful: false
            };
            return { ...state, remoteLogoutStatus: remoteLogoutStatus };
        }),
        on(clusterLogoutRequestSuccess, (state, { clusterName }): UserState => {
            let remoteLogoutStatus = { ...state.remoteLogoutStatus };
            remoteLogoutStatus[clusterName] = {
                ...remoteLogoutStatus[clusterName],
                inProgress: false,
                logoffSuccessful: true
            };
            return { ...state, remoteLogoutStatus: remoteLogoutStatus };
        }),
        on(clusterLogoutRequestFail, (state, { clusterName, onCall }): UserState => {
            let remoteLogoutStatus = { ...state.remoteLogoutStatus };
            remoteLogoutStatus[clusterName] = {
                ...remoteLogoutStatus[clusterName],
                onCall: onCall,
                inProgress: false,
                logoffSuccessful: false
            };
            return { ...state, remoteLogoutStatus: remoteLogoutStatus };
        }),
        on(fetchAvailableLanguagesSuccess, (state, { languages }): UserState => {
            return { ...state, supportedLanguages: languages };
        }),
        on(fetchUserSelectableRolesSuccess, (state, { roles }): UserState => {
            return { ...state, selectedRole: state.selectedRole ? state.selectedRole : String(roles[0]) };
        }),
        on(clearUsersSubscribedToCluster, (state, { clusterName }): UserState => {
            let usersToRemove = Object.values(state.entities)
                .filter((existingUser) => existingUser.clusterName === clusterName)
                .map((user) => user.uuid);
            return adapter.removeMany(usersToRemove, state);
        }),
        on(redialAbandonedSuccess, (state): UserState => {
            return { ...state, mostRecentHandledCallIsAbandoned: true };
        }),
        on(answerSuccess, (state): UserState => {
            return { ...state, mostRecentHandledCallIsAbandoned: false };
        }),
        on(acknowledgeAbandonedComplete, (state, { callId }): UserState => {
            return { ...state, acknowledgeAbandonedCallId: callId };
        }),
        on(refuseAbandonedRequestSuccess, (state): UserState => {
            return { ...state, mostRecentHandledCallIsAbandoned: true };
        }),
        on(acdStatusChangeFail, (state): UserState => {
            return {
                ...state,
                agentStatus: state.previousAgentStatus || state.agentStatus,
                previousAgentStatus: null
            };
        }),
        on(fetchReasonCodesSuccess, (state, { reasonCodes }): UserState => {
            return { ...state, reasonCodes: reasonCodes };
        }))
});

export const selectBidState = (state: UserState) => state.bids;

export const { selectEntities: selectBidEntities } = bidAdapter.getSelectors();

const { selectEntities, selectAll } = adapter.getSelectors();

export const selectAllUsers = selectAll;
export const selectUserEntities = selectEntities;
